import React, { useCallback, useEffect, useState } from "react";
import { Modal, Box, Typography, Button, LinearProgress } from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";

type ModalExportExcelProps = {
  open: boolean;
  messageExport: string;
  origin: 'export' | 'busca'
  handleCancelar: (origin: 'export' | 'busca') => void
};

const ModalExportExcel: React.FC<ModalExportExcelProps> = ({ open, messageExport, origin, handleCancelar }) => {
  const [haveClickCancel, setHaveClickCancel] = useState(false)

  useEffect(() => {
    if (!open) {
      setHaveClickCancel(false)
    }
  }, [open])

  const handleCancelClick = useCallback(() => {
    setHaveClickCancel(true)
    handleCancelar(origin)
  }, [handleCancelar, origin])

  const getMessage = useCallback((messageExport: string) => {
    return messageExport.split('|').filter(Boolean).map((message, index) => (
      <Typography
        key={`${message}-${index}`}
        variant="body1"
        sx={{
          color: index === 0 ? "#1a3661" : "text.secondary",
          fontWeight: index === 0 ? 600 : 400,
          fontSize: index === 0 ? "0.95rem" : "0.82rem",
          lineHeight: 1.5
        }}>
        {message}
      </Typography>
    ))

  }, [])

  return (
    <Modal
      open={open}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
      slotProps={{ backdrop: { sx: { backgroundColor: "rgba(7, 22, 43, 0.62)", backdropFilter: "blur(2px)" } } }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          boxShadow: "0 24px 64px rgba(7, 22, 43, 0.26)",
          px: { xs: 3, sm: 5 },
          py: { xs: 4, sm: 5 },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          borderRadius: 4,
          width: "calc(100% - 40px)",
          maxWidth: 480,
          textAlign: "center",
          overflow: "hidden",
          outline: "none",
          border: "1px solid rgba(26, 54, 97, 0.08)",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: "0 0 auto 0",
            height: 5,
            background: "linear-gradient(90deg, #102a4c 0%, #1976d2 55%, #57a6e6 100%)"
          }
        }}
      >
        <Box sx={{
          width: 64,
          height: 64,
          borderRadius: "20px",
          display: "grid",
          placeItems: "center",
          color: "#1976d2",
          bgcolor: "rgba(25, 118, 210, 0.09)",
          mb: 2.5
        }}>
          {origin === 'export'
            ? <FileDownloadRoundedIcon sx={{ fontSize: 32 }} />
            : <SearchRoundedIcon sx={{ fontSize: 34 }} />}
        </Box>

        <Typography id="modal-title" variant="h4" sx={{ color: "#102a4c", fontWeight: 700, fontSize: { xs: "1.55rem", sm: "1.8rem" } }}>
          {origin === 'export' ? 'Gerando Excel' : 'Buscando Produtos'}
        </Typography>
        <Typography id="modal-description" sx={{ mt: 1, color: "text.secondary", fontSize: "0.98rem" }}>
          {origin === 'export'
            ? 'Estamos organizando seus dados para o download.'
            : 'Estamos localizando e preparando os produtos para você.'}
        </Typography>

        <Box sx={{ width: "100%", mt: 3.5 }}>
          <LinearProgress
            aria-label="Carregando"
            sx={{
              height: 8,
              borderRadius: 999,
              bgcolor: "rgba(25, 118, 210, 0.12)",
              "& .MuiLinearProgress-bar": {
                borderRadius: 999,
                background: "linear-gradient(90deg, #15345f, #2494e8)"
              }
            }}
          />
          <Typography sx={{ mt: 1.25, color: "text.secondary", fontSize: "0.78rem", letterSpacing: "0.02em" }}>
            Isso pode levar alguns instantes
          </Typography>
        </Box>

        {!!messageExport && (
          <Box sx={{
            width: "100%",
            mt: 2.5,
            py: 1.5,
            px: 2,
            borderRadius: 2.5,
            bgcolor: "#f5f8fc",
            border: "1px solid rgba(26, 54, 97, 0.08)"
          }}>
            {getMessage(messageExport)}
          </Box>
        )}

        <Button
          variant="outlined"
          disabled={haveClickCancel}
          onClick={handleCancelClick}
          sx={{ mt: 3, px: 3.5, borderRadius: 2, textTransform: "none", fontWeight: 600 }}
        >
          {haveClickCancel ? 'Cancelando...' : 'Cancelar operação'}
        </Button>
      </Box>
    </Modal>
  );
};

export default ModalExportExcel;
